import { fieldText } from "@/lib/record-fields";
import type { RecordField, RecordSection } from "@/lib/types";

/**
 * A lead's emergency contact (ACL-382): who the rep calls when the firm cannot reach its client.
 *
 * The platform keeps it as one answer on the contact form (`emergency_contact`, a text field in
 * Required Intake Questions, as GoHighLevel had it), typed in words: "John Smith (Spouse)
 * 555-123-4567", or the same three in any order, or only some of them. Nothing stores the name,
 * the relationship and the number apart, so they are read out of the words here, for the card on
 * the record and the Call dialog. What cannot be read as a name is kept as a note, never dropped.
 *
 * The call is placed for the lead (`POST /dialer/calls` with `lead_id`): core-api checks the number
 * is in this answer and the call is then the lead's, on their record, never on a record found by
 * the number. Only through a campaign: from the rep's own phone it would be on nobody's record.
 * Pure, for `test/emergency-contact.test.ts`.
 */

/** The contact form's field, by its key: an admin may rename its title, never its key. */
export const EMERGENCY_CONTACT_KEY = "emergency_contact";

export interface EmergencyContact {
  name: string | null;
  /** As the answer wrote it, with a capital: "Spouse", "Mom", "Esposa". */
  relationship: string | null;
  /** The first number in the answer, as written. */
  phone: string | null;
  /** How many more numbers the answer has: only the first is offered to call. */
  moreNumbers: number;
  /** What is left of the answer when it is too long to be a name: a sentence about the person. */
  note: string | null;
}

/** The field and the folder it is in, when the server sent it: a field hidden from the reader's role
 *  is not in the answer, and then there is nothing to show at all. */
export function emergencyField(
  sections: readonly RecordSection[],
): { field: RecordField; section: RecordSection } | null {
  for (const section of sections) {
    const field = section.fields.find((f) => f.key === EMERGENCY_CONTACT_KEY);
    if (field) return { field, section };
  }
  return null;
}

/** The field's answer read as an emergency contact, or null when it has none. */
export function emergencyContactOf(field: RecordField): EmergencyContact | null {
  return parseEmergencyContact(fieldText(field));
}

/**
 * Relationships an answer names, English and the Spanish the book is written in, longest first so
 * "mother-in-law" is not read as "mother". Left out on purpose: words that are also first names
 * (Tia, Tio without the accent), so "Tia Brown" stays a name.
 */
const RELATIONSHIPS = [
  "mother-in-law", "father-in-law", "sister-in-law", "brother-in-law", "son-in-law", "daughter-in-law",
  "mother in law", "father in law", "sister in law", "brother in law", "son in law", "daughter in law",
  "granddaughter", "grandmother", "grandfather", "stepdaughter", "stepmother", "stepfather",
  "stepbrother", "stepsister", "best friend", "girlfriend", "boyfriend", "co-worker", "coworker",
  "caregiver", "grandson", "stepson", "guardian", "roommate", "neighbour", "neighbor", "daughter",
  "brother", "sibling", "husband", "partner", "fiancée", "fiancee", "fiancé", "fiance", "spouse",
  "mother", "father", "sister", "parent", "grandma", "grandpa", "cousin", "nephew", "friend",
  "mommy", "daddy", "child", "niece", "uncle", "aunt", "wife", "mom", "mum", "dad", "son",
  "esposa", "esposo", "madre", "padre", "mamá", "mama", "papá", "hija", "hijo", "hermana", "hermano",
  "abuela", "abuelo", "tía", "tío", "prima", "primo", "amiga", "amigo", "novia", "novio", "suegra",
  "suegro", "cuñada", "cuñado", "sobrina", "sobrino", "vecina", "vecino",
];

const LETTER = "\\p{L}";
const RELATIONSHIP = new RegExp(
  `(?<!${LETTER})(${RELATIONSHIPS.map((r) => r.replace(/[-\s]/g, "[-\\s]")).join("|")})(?!${LETTER})`,
  "iu",
);

/** A US or Canadian number however it was written: (555) 123-4567, 555.123.4567, +1 555 123 4567. */
const PHONE = /(?<!\d)(?:\+?1[\s.\-‐-―]*)?\(?\d{3}\)?[\s.\-‐-―]*\d{3}[\s.\-‐-―]*\d{4}(?!\d)/g;

/** Words that label a part of the answer rather than say it: "Name:", "Phone #", "her husband",
 *  and the "or" between two numbers. */
const LABELS = new RegExp(
  `(?<!${LETTER})(?:emergency|contact|name|relationship|relation|rel|phone|ph|cell|mobile|tel|number|num|no|his|her|their|my|client's|clients|or|and)(?!${LETTER})\\.?`,
  "giu",
);

/** An answer that says there is nobody: the intake form's field was filled to get past it. */
const NO_ANSWER = /^(?:n\/?a|none|no|nope|nobody|unknown|not given|not provided|declined|refused|no tiene|ninguno|ninguna|[-.\s]+)$/i;

/** Longer than this, what is left is a sentence about the person, not their name. */
const NAME_WORDS = 4;
const NAME_LENGTH = 40;

export function parseEmergencyContact(written: string | null | undefined): EmergencyContact | null {
  const text = (written ?? "").trim();
  if (!text || NO_ANSWER.test(text)) return null;

  const numbers = text.match(PHONE) ?? [];
  let rest = text.replace(PHONE, " ");

  const found = RELATIONSHIP.exec(rest);
  const relationship = found ? capitalise(found[1]!.toLowerCase().replace(/\s+/g, " ")) : null;
  if (found) rest = rest.slice(0, found.index) + " " + rest.slice(found.index + found[0].length);

  rest = rest
    .replace(LABELS, " ")
    .replace(/\(\s*\)|\[\s*\]/g, " ")
    // A dash between words separates parts; one inside a name ("Mary-Jane") is kept.
    .replace(/(^|\s)[-‐-―:#*]+(?=\s|$)/g, " ");
  const left = rest
    .split(/[,;|/\\:]+/)
    .map((part) => part.replace(/[\s()[\]{}#*.]+$/g, "").replace(/^[\s()[\]{}#*.]+/g, "").replace(/\s+/g, " ").trim())
    .filter((part) => /\p{L}/u.test(part))
    .join(", ");

  const words = left ? left.split(" ").length : 0;
  const isName = left !== "" && words <= NAME_WORDS && left.length <= NAME_LENGTH;
  return {
    name: isName ? left : null,
    relationship,
    phone: numbers[0]?.trim() ?? null,
    moreNumbers: Math.max(0, numbers.length - 1),
    note: left !== "" && !isName ? left : null,
  };
}

/**
 * Who the rep is about to call, for the Call dialog: "John Smith (Spouse), emergency contact for
 * Jane Smith". The lead is named so the rep knows on whose behalf they are calling.
 */
export function emergencyCallWho(
  contact: Pick<EmergencyContact, "name" | "relationship">,
  leadName: string | null,
): string {
  const lead = leadName ?? "this contact";
  if (contact.name) {
    return `${contact.name}${contact.relationship ? ` (${contact.relationship})` : ""}, emergency contact for ${lead}`;
  }
  if (contact.relationship) return `${contact.relationship} of ${lead}, their emergency contact`;
  return `Emergency contact for ${lead}`;
}

/** Said in the Call dialog of a call for a lead: where it is saved, or, from the rep's own phone,
 *  why Call is dimmed. */
export function callForLeadLine(leadName: string | null, viaCampaign: boolean, asRep = false): string {
  const lead = leadName ?? "this contact";
  if (asRep && !viaCampaign) return `Go Ready to call for ${lead}: only then is the call saved on their record.`;
  return viaCampaign
    ? `The call is saved on ${lead}'s record.`
    : `Go Ready on a campaign to call for ${lead}: only a call through a campaign is saved on their record.`;
}

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
